import { authRequest } from "@api/auth-requests";
import { AUTH_API_ROUTES, AUTH_PRODUCT } from "@constants/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { handleApiError } from "@utils/handle-api-error";
import { tokenInstance } from "@utils/token-instance";
import { type AxiosError, HttpStatusCode } from "axios";

import { useAuth } from "../utils/use-auth";

export interface LoginRequest {
	login: string;
	password: string;
	isLdap?: boolean;
}

interface LoginResponse {
	active_token: string | null;
	refresh_token: string | null;
	/**
	 * Only sent while the account still holds its emailed first-time password.
	 * The gateway mostly answers that case with a 425 instead — see `isTempPasswordError`.
	 */
	is_first_time?: boolean;
	/** Former name of `is_first_time`. */
	is_temp_password?: boolean;
}

/** Only written for a temporary password — TempPassword sends it along with the new one. */
interface LoginCache {
	credentials: LoginRequest;
}

export const LOGIN_QUERY_KEY = ["login"];

export async function loginRequest(args: LoginRequest) {
	const { isLdap, ...credentials } = args;
	const route = isLdap ? AUTH_API_ROUTES.LOGIN_LDAP : AUTH_API_ROUTES.LOGIN;
	const response = await authRequest
		// The gateway signs one user into several products; without a product
		// the session is issued against its default one instead of this app.
		.post<LoginResponse>(route, { ...credentials, product: AUTH_PRODUCT })
		.then((response) => response.data);
	return response;
}

/** The gateway refuses a sign-in with the emailed first-time password as 425 Too Early. */
const isTempPasswordError = (error: AxiosError) =>
	error.response?.status === HttpStatusCode.TooEarly;

export function useLoginRequest() {
	const navigate = useNavigate();
	const { login } = useAuth();

	const queryClient = useQueryClient();

	// Cached before leaving: TempPassword sends the user back to /login
	// without it.
	const goToTempPassword = (credentials: LoginRequest) => {
		queryClient.setQueryData<LoginCache>(LOGIN_QUERY_KEY, { credentials });
		navigate({
			to: "/login/temp",
			search: () => ({ strength: "0" }),
			replace: true,
			viewTransition: true,
		});
	};

	const { mutate, isPending, data } = useMutation<
		LoginResponse,
		AxiosError,
		LoginRequest,
		unknown
	>({
		mutationFn: (args) => loginRequest(args),
		// The variables are the password — not kept once the form is gone.
		gcTime: 0,
		onSuccess: (data, variables) => {
			const { is_first_time, is_temp_password, active_token, refresh_token } =
				data;

			if (is_first_time || is_temp_password) {
				goToTempPassword(variables);
				return;
			} else if (active_token) {
				// An earlier attempt with a temporary password may have left it here.
				queryClient.removeQueries({ queryKey: LOGIN_QUERY_KEY });
				tokenInstance.setToken(active_token);
				if (refresh_token) tokenInstance.setRefreshToken(refresh_token);
				login();
			}
		},
		onError: (err, variables) => {
			if (isTempPasswordError(err)) {
				goToTempPassword(variables);
				return;
			}

			handleApiError(err);
		},
	});

	const { data: cachedLoginData } = useQuery<LoginCache>({
		queryKey: LOGIN_QUERY_KEY,
		enabled: false,
		staleTime: Infinity,
		gcTime: 1000 * 60 * 5,
	});

	return {
		loginMutate: mutate,
		loginMutatePending: isPending,
		loginMutateData: data,
		loginMutateCachedData: cachedLoginData,
		loginMutateClearCache: () =>
			queryClient.removeQueries({ queryKey: LOGIN_QUERY_KEY }),
	};
}
